export class AudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private ambientNodes: AudioNode[] = [];
  private muted = false;
  private noiseBuffer: AudioBuffer | null = null;
  private musicElement: HTMLAudioElement | null = null;
  private musicUrl: string | null = null;
  private musicVolume = 0.35;
  private usingExternalMusic = false;

  private readonly musicCandidates: string[] = [
    '/assets/jurassic-theme.ogg',
    '/assets/jurassic-theme.mp3',
    '/assets/jurassic-theme.wav',
    '/assets/music.ogg',
    '/assets/music.mp3',
  ];

  init(): void {
    if (this.ctx) return;
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.6;
    this.masterGain.connect(this.ctx.destination);

    const length = this.ctx.sampleRate * 2;
    const buffer = this.ctx.createBuffer(1, length, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * 0.3;
    }
    this.noiseBuffer = buffer;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.masterGain) this.masterGain.gain.value = m ? 0 : 0.6;
    if (this.musicElement) {
      this.musicElement.volume = m ? 0 : this.musicVolume;
    }
  }

  async tryLoadMusicFile(): Promise<boolean> {
    for (const url of this.musicCandidates) {
      const ok = await this.tryMusicUrl(url);
      if (ok) {
        this.musicUrl = url;
        this.usingExternalMusic = true;
        console.info(`[audio] using external music: ${url}`);
        return true;
      }
    }
    console.info('[audio] no external music file found, using synthesized ambient');
    return false;
  }

  private tryMusicUrl(url: string): Promise<boolean> {
    return new Promise((resolve) => {
      const el = new Audio();
      el.loop = true;
      el.volume = this.muted ? 0 : this.musicVolume;
      el.preload = 'auto';
      const cleanup = () => {
        el.removeEventListener('canplaythrough', onOk);
        el.removeEventListener('error', onErr);
        el.removeAttribute('src');
      };
      const onOk = () => {
        cleanup();
        el.play().then(
          () => {
            this.musicElement = el;
            resolve(true);
          },
          () => {
            resolve(false);
          }
        );
      };
      const onErr = () => {
        cleanup();
        resolve(false);
      };
      el.addEventListener('canplaythrough', onOk);
      el.addEventListener('error', onErr);
      el.src = url;
    });
  }

  isUsingExternalMusic(): boolean {
    return this.usingExternalMusic;
  }

  stopMusic(): void {
    if (this.musicElement) {
      this.musicElement.pause();
      this.musicElement.currentTime = 0;
    }
  }

  startAmbient(): void {
    if (!this.ctx || !this.masterGain) return;
    if (this.ambientNodes.length > 0) return;
    if (this.usingExternalMusic) return;
    const noise = this.ctx.createBufferSource();
    if (this.noiseBuffer) noise.buffer = this.noiseBuffer;
    noise.loop = true;
    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 400;
    const gain = this.ctx.createGain();
    gain.gain.value = 0.15;
    noise.connect(lp).connect(gain).connect(this.masterGain);
    noise.start();
    this.ambientNodes.push(noise, lp, gain);

    const droneOsc = this.ctx.createOscillator();
    droneOsc.type = 'sine';
    droneOsc.frequency.value = 55;
    const droneGain = this.ctx.createGain();
    droneGain.gain.value = 0.08;
    const droneLfo = this.ctx.createOscillator();
    droneLfo.frequency.value = 0.1;
    const droneLfoGain = this.ctx.createGain();
    droneLfoGain.gain.value = 8;
    droneLfo.connect(droneLfoGain).connect(droneOsc.frequency);
    droneOsc.connect(droneGain).connect(this.masterGain);
    droneOsc.start();
    droneLfo.start();
    this.ambientNodes.push(droneOsc, droneGain, droneLfo, droneLfoGain);
  }

  stopAmbient(): void {
    for (const n of this.ambientNodes) {
      try {
        if ('stop' in n && typeof (n as OscillatorNode).stop === 'function') {
          (n as OscillatorNode).stop();
        }
      } catch {
        // ignore
      }
    }
    this.ambientNodes = [];
  }

  playWind(): void {
    if (!this.ctx || !this.masterGain) return;
    const t = this.ctx.currentTime;
    const noise = this.ctx.createBufferSource();
    if (this.noiseBuffer) noise.buffer = this.noiseBuffer;
    noise.loop = true;
    const hp = this.ctx.createBiquadFilter();
    hp.type = 'bandpass';
    hp.frequency.setValueAtTime(800, t);
    hp.frequency.exponentialRampToValueAtTime(200, t + 0.4);
    hp.Q.value = 2;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.4, t + 0.05);
    g.gain.linearRampToValueAtTime(0, t + 0.4);
    noise.connect(hp).connect(g).connect(this.masterGain);
    noise.start(t);
    noise.stop(t + 0.5);
  }

  playImpact(): void {
    if (!this.ctx || !this.masterGain) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(180, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.4);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.5, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    osc.connect(g).connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 0.5);
  }

  playShield(): void {
    if (!this.ctx || !this.masterGain) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(220, t);
    osc.frequency.exponentialRampToValueAtTime(880, t + 0.15);
    osc.frequency.exponentialRampToValueAtTime(440, t + 0.5);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.35, t + 0.05);
    g.gain.linearRampToValueAtTime(0.2, t + 0.3);
    g.gain.linearRampToValueAtTime(0, t + 0.6);
    osc.connect(g).connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 0.6);
  }

  playRoar(): void {
    if (!this.ctx || !this.masterGain) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(120, t);
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.3);
    osc.frequency.exponentialRampToValueAtTime(30, t + 0.7);
    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 600;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.3, t + 0.05);
    g.gain.linearRampToValueAtTime(0, t + 0.7);
    osc.connect(lp).connect(g).connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 0.7);
  }

  playConsumed(): void {
    if (!this.ctx || !this.masterGain) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(50, t);
    osc.frequency.linearRampToValueAtTime(20, t + 1.5);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.6, t + 0.2);
    g.gain.linearRampToValueAtTime(0, t + 1.5);
    osc.connect(g).connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 1.5);
  }

  playRespawn(): void {
    if (!this.ctx || !this.masterGain) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(440, t);
    osc.frequency.exponentialRampToValueAtTime(1320, t + 0.4);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.3, t + 0.05);
    g.gain.linearRampToValueAtTime(0, t + 0.4);
    osc.connect(g).connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 0.4);
  }

  startHeartbeat(intervalMs: number = 800): () => void {
    const ctx = this.ctx;
    const master = this.masterGain;
    if (!ctx || !master) return () => {};
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(60, t);
      osc.frequency.exponentialRampToValueAtTime(30, t + 0.15);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.5, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
      osc.connect(g).connect(master);
      osc.start(t);
      osc.stop(t + 0.2);
    };
    const id = window.setInterval(tick, intervalMs);
    tick();
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }
}

export const audio = new AudioEngine();
